import { useEffect, useState } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { parseEntityRef } from '@backstage/catalog-model';
import { Button, Typography, makeStyles } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/ReportProblemOutlined';
import PeopleIcon from '@material-ui/icons/PeopleOutline';
import ShieldIcon from '@material-ui/icons/SecurityOutlined';
import { SYSTEMS, type SystemId } from './systems';
import { RequestPanel } from './RequestPanel';

const useStyles = makeStyles(theme => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    border: '1px solid rgba(219, 171, 10, 0.55)',
    background: 'rgba(253, 229, 73, 0.14)',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(3),
  },
  bannerIcon: { color: 'var(--ui-warn-border)' },
  bannerText: { flex: 1, minWidth: 220, lineHeight: 1.5 },
  ref: { fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.75fr) minmax(0, 1fr)',
    gap: theme.spacing(3),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 2),
    marginBottom: theme.spacing(1.5),
  },
  heading: { fontSize: '1rem', fontWeight: 600, margin: 0 },
  aside: { fontSize: '0.8125rem', color: 'var(--ui-text-muted)' },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  card: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    textAlign: 'left',
    width: '100%',
    padding: theme.spacing(1.75, 2),
    borderRadius: 8,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text)',
    font: 'inherit',
    cursor: 'pointer',
    '&:hover': { borderColor: 'var(--ui-primary)' },
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
  },
  cardIcon: {
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    width: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid var(--ui-border)',
    color: 'var(--ui-text-muted)',
  },
  cardText: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  cardTitle: { fontWeight: 600, fontSize: '0.9375rem' },
  cardWhat: { fontSize: '0.8125rem', color: 'var(--ui-text-muted)' },
  cardWho: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
  },
  how: {
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    padding: theme.spacing(2, 2.25),
  },
  howHead: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  rules: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    color: 'var(--ui-text-muted)',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    '& li + li': { marginTop: theme.spacing(0.5) },
  },
  links: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

/** Where *Manage secrets* leads: Infisical (secrets/README.md in `secrets`). */
const SECRETS_URL = 'https://secrets.revenexx.com';

/**
 * Where *SSH via NetBird* leads: the handbook's guide, from the atlas
 * repository's handbook/tutorials/ssh-via-netbird.md as its docs site serves
 * it.
 */
const SSH_GUIDE_URL =
  'https://atlas.revenexx.dev/handbook/tutorials/ssh-via-netbird';

type Ownership =
  | { loading: true }
  | { loading: false; userEntityRef: string; inTeam: boolean };

/**
 * Whether the signed-in colleague's sign-in names a team (specs/access.md AC-1
 * and AC-2).
 *
 * Read off the ownership refs alone, never off where they came from: today the
 * sign-in builds them from the identity provider's `teams` profile field, and
 * whether membership stays there or moves to GitHub teams is open (DX-202). A
 * catalog `memberOf` would not do — the portal holds no User entities.
 */
function useOwnership(): Ownership {
  const identityApi = useApi(identityApiRef);
  const [state, setState] = useState<Ownership>({ loading: true });
  useEffect(() => {
    let active = true;
    identityApi.getBackstageIdentity().then(identity => {
      const inTeam = identity.ownershipEntityRefs.some(ref => {
        try {
          return parseEntityRef(ref).kind.toLowerCase() === 'group';
        } catch {
          return false;
        }
      });
      if (active) {
        setState({
          loading: false,
          userEntityRef: identity.userEntityRef,
          inTeam,
        });
      }
    });
    return () => {
      active = false;
    };
  }, [identityApi]);
  return state;
}

/**
 * The Access page (DX-342; promised in specs/access.md): ask for access to what
 * the platform runs, the request going to the owner the catalog names.
 *
 * Stage 1 of the target picture's page. Its approvals, open requests, expiring
 * grants and the access a colleague holds need a record of grants the portal
 * does not keep yet (DX-343), so they are left off rather than drawn with
 * examples. The layout keeps their places: the cards on the left, the narrower
 * column on the right, which they will join.
 */
export const AccessPage = () => {
  const classes = useStyles();
  const ownership = useOwnership();
  const [panel, setPanel] = useState<{ systemId: SystemId; key: number }>();
  const openPanel = (systemId: SystemId) =>
    setPanel(current => ({ systemId, key: (current?.key ?? 0) + 1 }));

  return (
    <Page themeId="home">
      <Header
        title="Access"
        subtitle="What you have, what you owe an answer on, and how to ask for more. Requests go to the owner in the catalog; grants are logged and production access expires by default."
      />
      <Content>
        {!ownership.loading && !ownership.inTeam && (
          <div
            role="region"
            aria-label="Group membership"
            className={classes.banner}
          >
            <WarningIcon className={classes.bannerIcon} />
            <Typography variant="body2" className={classes.bannerText}>
              <strong>Your user is not in a group.</strong> Ownership, owned
              services and “needs your approval” stay empty until{' '}
              <span className={classes.ref}>{ownership.userEntityRef}</span> is
              a member of a group.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => openPanel('team-membership')}
            >
              Request team membership
            </Button>
          </div>
        )}

        <div className={classes.layout}>
          <section aria-labelledby="access-request-heading">
            <div className={classes.sectionHead}>
              <h2 id="access-request-heading" className={classes.heading}>
                Request access
              </h2>
              <span className={classes.aside}>
                Requests open a Linear issue in the owner's team
              </span>
            </div>
            <div className={classes.cards}>
              {SYSTEMS.map(system => {
                const Icon = system.icon;
                return (
                  <button
                    key={system.id}
                    type="button"
                    className={classes.card}
                    onClick={() => openPanel(system.id)}
                  >
                    <span className={classes.cardIcon} aria-hidden>
                      <Icon fontSize="small" />
                    </span>
                    <span className={classes.cardText}>
                      <span className={classes.cardTitle}>{system.title}</span>
                      <span className={classes.cardWhat}>
                        {system.description}
                      </span>
                      <span className={classes.cardWho}>
                        <PeopleIcon fontSize="inherit" aria-hidden />
                        {system.approver} · {system.typical}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section aria-labelledby="access-how-heading" className={classes.how}>
            <div className={classes.howHead}>
              <ShieldIcon fontSize="small" aria-hidden />
              <h2 id="access-how-heading" className={classes.heading}>
                How access works here
              </h2>
            </div>
            <ul className={classes.rules}>
              <li>Zitadel is the master account; most tools follow via SSO.</li>
              <li>
                The approver is the <strong>owner in the catalog</strong> —
                that's why ownership matters.
              </li>
              <li>Production access is time-boxed (7–90 days) and logged.</li>
              <li>
                Secrets never travel by chat: Infisical scope or Passbolt.
              </li>
            </ul>
            <div className={classes.links}>
              <Button
                variant="outlined"
                size="small"
                href={SECRETS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Manage secrets
              </Button>
              <Button
                variant="outlined"
                size="small"
                href={SSH_GUIDE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                SSH via NetBird
              </Button>
            </div>
          </section>
        </div>

        {panel && (
          <RequestPanel
            key={panel.key}
            systemId={panel.systemId}
            onClose={() => setPanel(undefined)}
          />
        )}
      </Content>
    </Page>
  );
};
